import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Button } from "@/components/ui/button";
import { LogOut, Menu, X, Sun, Moon, Monitor, Download, ChevronDown, CalendarDays, ClipboardList, Trophy, Users, Shield, LayoutGrid, MessageCircle, Bell } from "lucide-react";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import CalendarSubscribeButton from "@/components/app/CalendarSubscribeButton";
import ImageUpload, { StorageImage } from "@/components/app/ImageUpload";
import FixturesChat from "@/components/app/FixturesChat";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { usePWAInstall } from "@/hooks/usePWAInstall";
import { UpdateBanner } from "@/components/app/UpdateBanner";
import { WhatsNewPopup } from "@/components/app/WhatsNewPopup";
import { PushPromptBanner } from "@/components/app/PushPromptBanner";
import { ActionsSummary } from "@/components/app/ActionsSummary";
import { ViewAsBanner } from "@/components/app/ViewAsBanner";

export default function AppShell({ children }) {
  const { user, logout, setUser, impersonating, exitImpersonation, featureFlags } = useAuth();
  const { theme, preference, toggle, setTheme } = useTheme();
  const themeLabel = preference === "system" ? `Following phone (${theme}) — tap for light` : preference === "light" ? "Light — tap for dark" : "Dark — tap to follow phone";
  const ThemeIcon = preference === "system" ? Monitor : preference === "light" ? Sun : Moon;
  const nav = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  useEffect(() => { setOpen(false); }, [location.pathname]);
  const [avatarOpen, setAvatarOpen] = useState(false);
  const [unreadNotices, setUnreadNotices] = useState(0);
  const [unreadChat, setUnreadChat] = useState(0);
  useEffect(() => {
    if (!user) return;
    const refresh = () => api.get("/chat/unread").then(r => setUnreadChat(r.data.total || 0)).catch(() => {});
    refresh();
    const id = setInterval(refresh, 15000);
    return () => clearInterval(id);
  }, [user, location.pathname]);
  useEffect(() => {
    if (!user || user.role === "vendor") return;
    const refresh = () => api.get("/notices").then(r => setUnreadNotices(r.data.unread || 0)).catch(() => {});
    refresh();
    window.addEventListener("notices-changed", refresh);
    const id = setInterval(refresh, 5 * 60 * 1000);
    return () => { window.removeEventListener("notices-changed", refresh); clearInterval(id); };
  }, [user]);
  const [pendingAdmin, setPendingAdmin] = useState(0);
  const [actionItems, setActionItems] = useState([]);
  const [actionData, setActionData] = useState(null);
  const refreshActions = () => api.get("/my/action-items").then(r => { setActionData(r.data); setActionItems(r.data.items || []); setPendingAdmin(r.data.total || 0); }).catch(() => {});
  useEffect(() => {
    if (!user || user.role === "vendor") return;
    const refresh = refreshActions;
    refresh();
    window.addEventListener("admin-queue-changed", refresh);
    const id = setInterval(refresh, 2 * 60 * 1000);
    return () => { window.removeEventListener("admin-queue-changed", refresh); clearInterval(id); };
  }, [user, location.pathname]);
  const Badge = ({ n, className = "" }) => n > 0 ? <span className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold leading-none ${className}`} data-testid="admin-pending-badge">{n > 99 ? "99+" : n}</span> : null;
  const { canInstall, install } = usePWAInstall();

  const isDark = theme === "dark";
  const isAdmin = user?.role === "admin";
  const isVendor = user?.role === "vendor";
  const isTreasurerAccount = user?.role === "treasurer";
  const financeUser = isAdmin || isTreasurerAccount || user?.is_treasurer === true || (user?.permissions || []).includes("expenses.pay");
  // Admins keep every link regardless of flag state; everyone else respects the toggle.
  const flagVisible = (flag) => isAdmin || featureFlags?.[flag] === true;

  // One grouped definition drives desktop dropdowns, the mobile sheet and the bottom bar.
  const byLabel = (a, b) => a.label.localeCompare(b.label);
  const groups = isVendor ? [] : isTreasurerAccount ? [
    { g: "Finance", items: [
      { to: "/app/expenses", label: "Expense Reclaim" },
      { to: "/app/fees", label: "Subscription Fees & Refunds" },
      { to: "/app/fines", label: "League Fines" },
      { to: "/app/referee-payments", label: "Referee Payments" },
      { to: "/app/reports", label: "Report Builder" },
      { to: "/app/committee-reports", label: "Committee Reports" },
    ] },
    { g: "Club", items: [
      { to: "/app/tasks", label: "Task Board" },
      { to: "/app/notices", label: "Notices" },
      { to: "/app/committee-contacts", label: "Committee Contacts" },
    ] },
    { g: "App Settings", items: [
      { to: "/app/account", label: "Account" },
      { to: "/app/feedback", label: "App Improvement Request" },
      { to: "/app/whats-new", label: "What's New" },
    ] },
  ] : [
    { g: "Bookings & Fixtures", items: [
      { to: "/app/bookings",   label: "My Bookings" },
      { to: "/app/fixtures",   label: "My Fixtures" },
      (user?.permissions || []).includes("referee.self") && { to: "/app/refereeing", label: "My Refereeing" },
      (isAdmin || user?.role === "coach") && { to: "/app/swap-board", label: "Swap Board" },
      (isAdmin || user?.role === "coach") && { to: "/app/friendlies", label: "Friendly Requests" },
      (isAdmin || user?.role === "coach") && { to: "/app/waiting-list", label: "Waiting List" },
      (user?.year_group_lead_ages || []).length > 0 && flagVisible("year_group") && { to: "/app/year-group", label: "Year Group" },
    ] },
    { g: "Club Info", items: [
      flagVisible("coaches")   && { to: "/app/coaches",    label: "Coaches" },
      { to: "/app/committee-contacts", label: "Committee Contacts" },
      flagVisible("documents") && { to: "/app/documents",  label: "Documents" },
      flagVisible("leagues")   && { to: "/app/leagues",    label: "Leagues" },
      { to: "/app/notices",    label: "Notices" },
      isAdmin                  && { to: "/app/admin/social-posters", label: "Social Posters" },
      flagVisible("referees")  && { to: "/app/referees",   label: "Referees" },
      { to: "/app/teams",      label: "Teams" },
      { to: "/app/pitches",    label: "Venues & Pitches" },
      isAdmin                  && { to: "/app/fa-updates", label: "FA Updates" },
    ] },
    { g: "Club Admin", items: [
      isAdmin                  && { to: "/app/admin",      label: "Admin Console", badge: pendingAdmin },
      { to: "/app/committee",  label: "Ask the Committee" },
      flagVisible("kit")       && { to: "/app/kit",        label: "Kit & Equipment Orders" },
      featureFlags?.player_registration === true && { to: "/app/players", label: "Player Registration" },
      (flagVisible("expenses") || financeUser) && { to: "/app/expenses",   label: "Expense Reclaim" },
      financeUser && !isAdmin  && { to: "/app/fees",       label: "Subscription Fees & Refunds" },
      financeUser && !isAdmin  && { to: "/app/referee-payments", label: "Referee Payments" },
      { to: "/app/incidents",  label: "Incidents & Accidents" },
      { to: "/app/first-aid",  label: "First Aid Kits" },
      (isAdmin || (user?.permissions || []).some(p => p.startsWith("tasks."))) && { to: "/app/tasks", label: "Task Board" },
      (user?.permissions || []).includes("committee.reports") && { to: "/app/committee-reports", label: "Committee Reports" },
    ] },
    { g: "App Settings", items: [
      { to: "/app/account",    label: "Account" },
      (isAdmin || user?.is_treasurer || user?.is_expense_approver || user?.is_kit_admin || user?.is_welfare_officer) && { to: "/app/reports", label: "Report Builder" },
      { to: "/app/feedback",   label: "App Improvement Request" },
      { to: "/app/whats-new",  label: "What's New" },
    ] },
  ].map(x => ({ ...x, items: x.items.filter(Boolean).sort(byLabel) })).filter(x => x.items.length);

  const primaryLinks = isVendor ? [{ to: "/app/calendar", label: "Calendar" }, { to: "/app/account", label: "Account" }] : isTreasurerAccount ? [{ to: "/app/finance", label: "Finance" }] : [{ to: "/app/calendar", label: "Calendar" }];
  const chatLink = { to: "/app/chat", label: "Chat", badge: unreadChat };
  if (isVendor) primaryLinks.push(chatLink);
  const allGroupLinks = groups.flatMap(x => x.items);
  const groupActive = (x) => x.items.some(l => location.pathname.startsWith(l.to));
  const moreActive = allGroupLinks.some(l => location.pathname.startsWith(l.to));
  const mobileGroups = groups;
  const tabIcon = { "/app/calendar": CalendarDays, "/app/chat": MessageCircle, "/app/bookings": ClipboardList, "/app/fixtures": Trophy, "/app/teams": Users, "/app/admin": Shield, "/app/account": Users, "/app/finance": LayoutGrid, "/app/expenses": ClipboardList, "/app/tasks": ClipboardList };
  const bottomTabs = isVendor
    ? primaryLinks
    : isTreasurerAccount
    ? [{ to: "/app/finance", label: "Finance" }, { to: "/app/expenses", label: "Expenses" }, { to: "/app/tasks", label: "Tasks" }, { to: "/app/chat", label: "Chat" }]
    : [{ to: "/app/calendar", label: "Calendar" }, { to: "/app/bookings", label: "My Bookings" }, { to: "/app/fixtures", label: "My Fixtures" }, isAdmin ? { to: "/app/admin", label: "Admin" } : { to: "/app/teams", label: "Teams" }];
  const slug = (s) => s.toLowerCase().replace(/[’']/g, "").replace(/\s*&\s*/g, "-").replace(/\s+/g, "-");

  return (
    <div className={`min-h-screen grain ${isDark ? "bg-[#0A0F1C] text-white" : "bg-white text-[#0F172A]"}`}>
      <header className={`sticky top-0 z-50 backdrop-blur-xl border-b ${isDark ? "bg-[#0A0F1C]/80 border-white/10" : "bg-white/80 border-black/10"}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <Link to={isTreasurerAccount ? "/app/finance" : "/app/calendar"} className="flex items-center gap-3 flex-shrink-0 whitespace-nowrap" data-testid="nav-logo">
            <img src="/logo.png" alt="Elsenham YFC" className="w-10 h-10 rounded-full object-contain flex-shrink-0" />
            <div className="min-w-0">
              <div className="font-display font-black text-lg tracking-tight leading-none">ELSENHAM YFC</div>
              <div className={`text-[10px] tracking-[0.25em] uppercase ${isDark ? "text-white/50" : "text-black/50"}`}>Booking</div>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-0.5 flex-shrink min-w-0" data-testid="nav-primary">
            {primaryLinks.map(l => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-${slug(l.label)}`}
                className={({isActive}) =>
                  `px-3 py-2 text-sm font-medium tracking-wide whitespace-nowrap transition-colors ${
                    isActive ? "text-[#83B68B]" : (isDark ? "text-white/70 hover:text-white" : "text-black/70 hover:text-black")
                  }`
                }
              >
                {l.label}{l.badge > 0 && <Badge n={l.badge} className="ml-1.5 align-middle" />}
              </NavLink>
            ))}
            {groups.map(x => (
              <DropdownMenu key={x.g}>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid={`nav-group-${slug(x.g)}`}
                    className={`px-3 py-2 text-sm font-medium tracking-wide whitespace-nowrap transition-colors inline-flex items-center gap-1 ${
                      groupActive(x) ? "text-[#83B68B]" : (isDark ? "text-white/70 hover:text-white" : "text-black/70 hover:text-black")
                    }`}
                  >
                    {x.g} <ChevronDown className="w-3.5 h-3.5" />
                    {x.g === "Club Info" && unreadNotices > 0 && <span className="w-1.5 h-1.5 rounded-full bg-[#83B68B]" />}
                    {x.g === "Club Admin" && isAdmin && <Badge n={pendingAdmin} />}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="min-w-[210px]">
                  {x.items.map(l => (
                    <DropdownMenuItem key={l.to} asChild>
                      <NavLink
                        to={l.to}
                        data-testid={`nav-more-${slug(l.label)}`}
                        className={({isActive}) =>
                          `w-full cursor-pointer px-2 py-1.5 text-sm ${isActive ? "text-[#83B68B]" : ""}`
                        }
                      >
                        {l.label}
                        {l.badge > 0 && <Badge n={l.badge} className="ml-2" />}
                        {l.to === "/app/notices" && unreadNotices > 0 && <span className="ml-2 text-[9px] px-1.5 py-0.5 rounded-full bg-[#83B68B] text-white" data-testid="notices-unread-badge">{unreadNotices}</span>}
                      </NavLink>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            ))}
            {pendingAdmin > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button type="button" data-testid="nav-todo" title="Needs your action" className={`relative px-2 py-2 text-sm font-medium whitespace-nowrap transition-colors inline-flex items-center gap-1 ${isDark ? "text-white/70 hover:text-white" : "text-black/70 hover:text-black"}`}>
                    <Bell className="w-4 h-4" /><Badge n={pendingAdmin} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-80" data-testid="admin-actions-menu">
                  <div className="px-2 py-1.5 text-[10px] tracking-widest uppercase opacity-60">Needs your action</div>
                  {actionItems.map(q => (
                    <DropdownMenuItem key={q.key} asChild>
                      <Link to={q.to} data-testid={`admin-action-${q.key}`} className="flex items-center justify-between gap-3 cursor-pointer">
                        <span>{q.label}</span><Badge n={q.count} />
                      </Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            {!isVendor && (
              <NavLink to={chatLink.to} data-testid="nav-chat"
                className={({isActive}) => `px-3 py-2 text-sm font-medium tracking-wide whitespace-nowrap transition-colors ${isActive ? "text-[#83B68B]" : (isDark ? "text-white/70 hover:text-white" : "text-black/70 hover:text-black")}`}>
                Chat{unreadChat > 0 && <Badge n={unreadChat} className="ml-1.5 align-middle" />}
              </NavLink>
            )}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            {canInstall && (
              <button
                onClick={install}
                data-testid="nav-pwa-install-btn"
                title="Add Elsenham YFC to your home screen"
                className="text-[10px] tracking-widest uppercase px-3 py-1.5 rounded border border-[#83B68B]/50 text-[#83B68B] hover:bg-[#83B68B]/10 inline-flex items-center gap-1.5 transition-colors"
              >
                <Download className="w-3 h-3" /> Install app
              </button>
            )}
            <CalendarSubscribeButton isDark={isDark} />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="theme-toggle-btn"
                  aria-label="Appearance"
                  title={themeLabel}
                  className={`p-2 rounded transition-colors ${isDark ? "text-white/70 hover:text-white hover:bg-white/10" : "text-black/60 hover:text-black hover:bg-black/5"}`}
                >
                  <ThemeIcon className="w-4 h-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[180px]" data-testid="theme-menu">
                {[["system", "Follow phone", Monitor], ["light", "Light", Sun], ["dark", "Dark", Moon]].map(([k, label, Icon]) => (
                  <DropdownMenuItem key={k} onSelect={() => setTheme(k)} data-testid={`theme-option-${k}`} className={`cursor-pointer gap-2 ${preference === k ? "text-[#83B68B]" : ""}`}>
                    <Icon className="w-4 h-4" /> {label}{k === "system" && preference === "system" ? ` · ${theme}` : ""}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <div className="text-right leading-tight">
              <div className={`text-xs ${isDark ? "text-white/50" : "text-black/50"}`}>Signed in</div>
              <div className="text-sm font-medium" data-testid="nav-user-name">{user?.name}</div>
            </div>
            <button
              onClick={() => setAvatarOpen(true)}
              data-testid="nav-avatar-btn"
              aria-label="Update your photo"
              className={`w-9 h-9 rounded-full overflow-hidden border-2 transition-colors ${isDark ? "border-white/20 hover:border-[#83B68B]" : "border-black/20 hover:border-[#83B68B]"}`}
            >
              {user?.avatar_path ? (
                <StorageImage path={user.avatar_path} alt={user?.name || ""} className="w-full h-full object-cover" />
              ) : (
                <div className={`w-full h-full flex items-center justify-center text-xs font-display font-black ${isDark ? "bg-white/10 text-white/70" : "bg-black/5 text-black/60"}`}>
                  {(user?.name || "?").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase()}
                </div>
              )}
            </button>
            <Button
              variant="ghost"
              size="sm"
              onClick={async () => { await logout(); nav("/"); }}
              data-testid="nav-logout-btn"
              className={isDark ? "text-white/70 hover:text-white hover:bg-white/10" : "text-black/60 hover:text-black hover:bg-black/5"}
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>

          <div className="md:hidden flex items-center gap-1">
            <button onClick={toggle} aria-label="Toggle theme" title={themeLabel} className="p-2.5 rounded-md active:bg-white/10">
              <ThemeIcon className="w-4 h-4" />
            </button>
            <button onClick={() => setOpen(v => !v)} data-testid="nav-mobile-toggle" aria-label={open ? "Close menu" : "Open menu"} className="p-2.5 rounded-md active:bg-white/10">
              {open ? <X /> : <Menu />}
            </button>
          </div>
        </div>
        {open && (
          <div className={`md:hidden border-t max-h-[calc(100dvh-64px)] overflow-y-auto pb-24 ${isDark ? "border-white/10 bg-[#0A0F1C]" : "border-black/10 bg-white"}`} data-testid="nav-mobile-sheet">
            <div className="px-4 pt-4 pb-2 grid grid-cols-2 gap-2">
              {[...primaryLinks, ...(isVendor ? [] : [chatLink])].map(l => {
                const Icon = tabIcon[l.to] || LayoutGrid;
                return (
                  <NavLink key={l.to} to={l.to} onClick={() => setOpen(false)} data-testid={`nav-mobile-${l.label.toLowerCase().replace(/\s+/g, '-')}`}
                    className={({ isActive }) => `flex items-center gap-2.5 px-3 py-3 rounded-md border text-sm font-medium ${isActive ? "border-[#83B68B] text-[#83B68B] bg-[#83B68B]/10" : (isDark ? "border-white/10" : "border-black/10")}`}>
                    <Icon className="w-4 h-4 flex-shrink-0" /> {l.label}{l.to === "/app/admin" && <Badge n={pendingAdmin} className="ml-auto" />}{l.badge > 0 && <Badge n={l.badge} className="ml-auto" />}
                  </NavLink>
                );
              })}
            </div>
            {actionItems.length > 0 && (
              <div className="px-4 pt-3" data-testid="nav-mobile-todo">
                <div className="text-[10px] tracking-[0.3em] uppercase text-red-400 pb-1">Needs your action</div>
                {actionItems.map(q => (
                  <Link key={q.key} to={q.to} onClick={() => setOpen(false)} className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-md text-sm" data-testid={`nav-mobile-action-${q.key}`}>
                    <span>{q.label}</span><Badge n={q.count} />
                  </Link>
                ))}
              </div>
            )}
            {mobileGroups.map(({ g, items }) => (
              <div key={g} className="px-4 pt-3">
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#83B68B] pb-1">{g}</div>
                <div className={`divide-y rounded-md border ${isDark ? "divide-white/5 border-white/10" : "divide-black/5 border-black/10"}`}>
                  {items.map(l => (
                    <NavLink key={l.to} to={l.to} onClick={() => setOpen(false)} data-testid={`nav-mobile-${slug(l.label)}`}
                      className={({ isActive }) => `flex items-center justify-between px-3 py-3 text-sm ${isActive ? "text-[#83B68B]" : ""}`}>
                        {l.label}
                      {l.badge > 0 && <Badge n={l.badge} />}
                      {l.to === "/app/notices" && unreadNotices > 0 && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#83B68B] text-white">{unreadNotices}</span>}
                    </NavLink>
                  ))}
                </div>
              </div>
            ))}
            <div className="px-4 pt-4 flex items-center gap-3">
              <button onClick={() => { setOpen(false); setAvatarOpen(true); }} className={`w-10 h-10 rounded-full overflow-hidden border-2 flex-shrink-0 ${isDark ? "border-white/20" : "border-black/20"}`} aria-label="Update your photo">
                {user?.avatar_path ? <StorageImage path={user.avatar_path} alt={user?.name || ""} className="w-full h-full object-cover" />
                  : <div className={`w-full h-full flex items-center justify-center text-xs font-display font-black ${isDark ? "bg-white/10" : "bg-black/5"}`}>{(user?.name || "?").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase()}</div>}
              </button>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{user?.name}</div>
                <div className="text-[10px] uppercase tracking-widest opacity-50">{user?.role}</div>
              </div>
              <button onClick={async () => { await logout(); nav("/"); }} data-testid="nav-mobile-logout" className="text-xs px-3 py-2 rounded border border-border opacity-80 inline-flex items-center gap-1.5"><LogOut className="w-3.5 h-3.5" /> Sign out</button>
            </div>
            {canInstall && (
              <div className="px-4 pt-3">
                <button onClick={() => { setOpen(false); install(); }} data-testid="nav-pwa-install-btn-mobile"
                  className="w-full py-2.5 text-sm text-[#83B68B] border border-[#83B68B]/40 rounded inline-flex items-center justify-center gap-2">
                  <Download className="w-3.5 h-3.5" /> Install app
                </button>
              </div>
            )}
          </div>
        )}
      </header>
      {impersonating && <ViewAsBanner user={user} onExit={async () => { await exitImpersonation(); window.location.href = "/app/admin/users"; }} />}
      {!isVendor && <ActionsSummary data={actionData} refresh={refreshActions} />}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10 pb-28 md:pb-10 relative z-10">
        {children}
      </main>

      <nav className={`md:hidden fixed bottom-0 inset-x-0 z-[60] border-t backdrop-blur-xl ${isDark ? "bg-[#0A0F1C]/90 border-white/10" : "bg-white/90 border-black/10"}`} style={{ paddingBottom: "env(safe-area-inset-bottom)" }} data-testid="nav-bottom-bar">
        <div className="grid" style={{ gridTemplateColumns: `repeat(${bottomTabs.length + 1}, minmax(0, 1fr))` }}>
          {bottomTabs.map(l => {
            const Icon = tabIcon[l.to];
            return (
              <NavLink key={l.to} to={l.to} onClick={() => setOpen(false)} data-testid={`nav-tab-${l.label.replace(/^My /, "").toLowerCase().replace(/\s+/g, '-')}`}
                className={({ isActive }) => `flex flex-col items-center justify-center gap-1 py-2.5 text-[10px] tracking-wider uppercase ${isActive ? "text-[#83B68B]" : (isDark ? "text-white/60" : "text-black/60")}`}>
                <span className="relative"><Icon className="w-5 h-5" />{l.to === "/app/admin" && pendingAdmin > 0 && <Badge n={pendingAdmin} className="absolute -top-2 -right-3" />}</span> <span className="truncate max-w-full px-1">{l.label.replace("My ", "")}</span>
              </NavLink>
            );
          })}
          <button type="button" onClick={() => setOpen(v => !v)} data-testid="nav-tab-more"
            className={`flex flex-col items-center justify-center gap-1 py-2.5 text-[10px] tracking-wider uppercase ${open || moreActive ? "text-[#83B68B]" : (isDark ? "text-white/60" : "text-black/60")}`}>
            <span className="relative"><LayoutGrid className="w-5 h-5" />{unreadNotices > 0 && <span className="absolute -top-1 -right-1.5 w-2 h-2 rounded-full bg-[#83B68B]" />}</span> More
          </button>
        </div>
      </nav>

      <FixturesChat />
      <UpdateBanner />
      <PushPromptBanner />
      <WhatsNewPopup />

      <Dialog open={avatarOpen} onOpenChange={setAvatarOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display tracking-tight text-2xl">Your photo</DialogTitle></DialogHeader>
          <div className="text-sm opacity-70 mb-2">Shown across the app next to your name.</div>
          <ImageUpload
            endpoint="/files/user/me"
            currentPath={user?.avatar_path}
            onUploaded={(r) => setUser && setUser({ ...user, avatar_path: r.storage_path })}
            shape="circle"
            testId="user-avatar-upload"
            label="Upload avatar"
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
